import { ColumnDef, Row, SortingFn } from '@tanstack/react-table';
import * as _ from 'lodash';

import { inspectDependencies } from '@finley-technologies/calc-agent';
import { utils as commonUtils } from '@finley-technologies/common';
import { CreditAgreement, Util } from '@finley-technologies/finley-types';

import {
  UpdateFundingVerificationReceivableEligibilityChangesetGQLType,
  VerificationEligibilityReceivableGQLType,
} from '../../../../types/dashboard-core-gqlTypes.generated.d';

// receivableId as a column header is used throughout as an anchor, so putting in a const
export const receivableIdColumnHeader: keyof VerificationEligibilityReceivableGQLType =
  'externalReceivableId';

export const receivableEligibilityDifferenceColumnHeaders: (keyof VerificationEligibilityReceivableGQLType)[] = [
  'waived',
  receivableIdColumnHeader,
  'exposure',
  'eligibleExposure',
  'eligibilityCriteriaKey',
  'borrowerEligible',
  'finleyEligibilityStatus',
  'borrowerFormula',
];

// this includes fields that we do not ever want to display
export const allReceivableEligibilityDifferenceFields = [
  ...receivableEligibilityDifferenceColumnHeaders,
  'id',
  'created_at',
  'updated_at',
  'fundingVerificationReportId',
];

// headers that are displayed at the eligibilityCriteria level, instead of receivable
// these headers will have column with rowSpan = 1, instead of merged down
export const individualEligibilityCriteriaColumnHeaders = [
  'eligibilityCriteriaKey',
  'borrowerEligible',
  'finleyEligibilityStatus',
  'borrowerFormula',
  'finleyFormula',
] as const;

// tanstack & chakra do not natively support freezing columns
// tanstack has 'columnPinning' but it's for changing column order. it does not have an UI (eg. freezing) associated
// so we will use css to achieve column freezing
export const waiveCheckboxColumnWidthPx = 80;
export const stickyColumnWidthPx = 175;
export const numberToPxStr = (pixels: number) => `${pixels}px`;
export const mainHeaderZIndex = 1;
export const freezeColumnZIndex = 2;
export const filterMenuZIndex = 3;

export type ColumnStyle = {
  width: string;
  minWidth: string;
  maxWidth: string;
  left: string;
  borderRightWidth?: string;
  zIndex: number;
};

export const selectModeFixedColumnsStyleToColumnIndexMap = [
  // select icon column
  {
    width: numberToPxStr(waiveCheckboxColumnWidthPx),
    minWidth: numberToPxStr(waiveCheckboxColumnWidthPx),
    maxWidth: numberToPxStr(waiveCheckboxColumnWidthPx),
    left: numberToPxStr(0),
    zIndex: freezeColumnZIndex,
  },
  // receivableId column
  {
    width: numberToPxStr(stickyColumnWidthPx),
    minWidth: numberToPxStr(stickyColumnWidthPx),
    maxWidth: numberToPxStr(stickyColumnWidthPx),
    left: numberToPxStr(waiveCheckboxColumnWidthPx),
    zIndex: freezeColumnZIndex,
  },
  // waive icon column
  {
    width: numberToPxStr(waiveCheckboxColumnWidthPx),
    minWidth: numberToPxStr(waiveCheckboxColumnWidthPx),
    maxWidth: numberToPxStr(waiveCheckboxColumnWidthPx),
    left: numberToPxStr(waiveCheckboxColumnWidthPx + stickyColumnWidthPx),
    zIndex: freezeColumnZIndex,
  },
  // exposure column
  {
    width: numberToPxStr(stickyColumnWidthPx),
    minWidth: numberToPxStr(stickyColumnWidthPx),
    maxWidth: numberToPxStr(stickyColumnWidthPx),
    left: numberToPxStr(stickyColumnWidthPx + waiveCheckboxColumnWidthPx * 2),
    zIndex: freezeColumnZIndex,
  },
  // eligible exposure column
  {
    width: numberToPxStr(stickyColumnWidthPx),
    minWidth: numberToPxStr(stickyColumnWidthPx),
    maxWidth: numberToPxStr(stickyColumnWidthPx),
    left: numberToPxStr(
      stickyColumnWidthPx * 2 + waiveCheckboxColumnWidthPx * 2,
    ),
    borderRightWidth: 'medium',
    zIndex: freezeColumnZIndex,
  },
];

const fixedColumnsStyleToColumnIndexMap = [
  // receivableId column
  {
    width: numberToPxStr(stickyColumnWidthPx),
    minWidth: numberToPxStr(stickyColumnWidthPx),
    maxWidth: numberToPxStr(stickyColumnWidthPx),
    left: numberToPxStr(0),
    zIndex: freezeColumnZIndex,
  },
  // waive icon column
  {
    width: numberToPxStr(waiveCheckboxColumnWidthPx),
    minWidth: numberToPxStr(waiveCheckboxColumnWidthPx),
    maxWidth: numberToPxStr(waiveCheckboxColumnWidthPx),
    left: numberToPxStr(stickyColumnWidthPx),
    zIndex: freezeColumnZIndex,
  },
  // exposure column
  {
    width: numberToPxStr(stickyColumnWidthPx),
    minWidth: numberToPxStr(stickyColumnWidthPx),
    maxWidth: numberToPxStr(stickyColumnWidthPx),
    left: numberToPxStr(stickyColumnWidthPx + waiveCheckboxColumnWidthPx),
    zIndex: freezeColumnZIndex,
  },
  // eligible exposure column
  {
    width: numberToPxStr(stickyColumnWidthPx),
    minWidth: numberToPxStr(stickyColumnWidthPx),
    maxWidth: numberToPxStr(stickyColumnWidthPx),
    left: numberToPxStr(stickyColumnWidthPx * 2 + waiveCheckboxColumnWidthPx),
    borderRightWidth: 'medium',
    zIndex: freezeColumnZIndex,
  },
];

export const getColumnStylesMap = ({
  showSelectMode,
}: {
  showSelectMode: boolean;
}): ColumnStyle[] => {
  if (showSelectMode) {
    return selectModeFixedColumnsStyleToColumnIndexMap;
  }

  return fixedColumnsStyleToColumnIndexMap;
};

export const selectActionRowStickyColumnStyle = {
  width: numberToPxStr(
    waiveCheckboxColumnWidthPx * 2 + stickyColumnWidthPx * 3,
  ),
  minWidth: numberToPxStr(
    waiveCheckboxColumnWidthPx * 2 + stickyColumnWidthPx * 3,
  ),
  maxWidth: numberToPxStr(
    waiveCheckboxColumnWidthPx * 2 + stickyColumnWidthPx * 3,
  ),
  left: numberToPxStr(0),
  zIndex: freezeColumnZIndex,
};

const receivableEligibilityDifferencesSelectActions = [
  'updateWaivers',
] as const;
export type ReceivableEligibilityDifferencesSelectActions = typeof receivableEligibilityDifferencesSelectActions[number];

export const allReceivableEligibilityDifferentSelectActionsOptions: {
  value: ReceivableEligibilityDifferencesSelectActions;
  label: string;
}[] = receivableEligibilityDifferencesSelectActions.map((action) => ({
  value: action,
  label: commonUtils.toTitleCase(action),
}));

export const receivableEligibilityDifferencesSelectActionToVerbMap: {
  [key in ReceivableEligibilityDifferencesSelectActions]: string;
} = { updateWaivers: 'update' };

export const toggleCheckboxDataForAllRows = ({
  columnId,
  currentValue,
  setData,
  currentData,
}: {
  columnId: string;
  currentValue: boolean;
  setData: ({
    dataForLoanProduct,
  }: {
    dataForLoanProduct: VerificationEligibilityReceivableGQLType[];
  }) => void;
  currentData: VerificationEligibilityReceivableGQLType[];
}) => {
  const newData = _.map(currentData, (currentRow) => {
    return {
      ...currentRow!,
      [columnId]: !currentValue,
    };
  });
  setData({
    dataForLoanProduct: newData,
  });
};

export const toggleCheckboxDataForRow = ({
  rowIndex,
  columnId,
  currentValue,
  setData,
  currentData,
}: {
  rowIndex: number;
  columnId: string;
  currentValue: boolean;
  setData: ({
    dataForLoanProduct,
  }: {
    dataForLoanProduct: VerificationEligibilityReceivableGQLType[];
  }) => void;
  currentData: VerificationEligibilityReceivableGQLType[];
}) => {
  const externalReceivableId = currentData[rowIndex].externalReceivableId;

  // since there may be multiple rows for the same receivable, per criteria
  // set all rows for the receivable to the same value
  const newData = _.map(currentData, (currentRow, index) => {
    if (currentRow.externalReceivableId === externalReceivableId) {
      return {
        ...currentData[index]!,
        [columnId]: !currentValue,
      };
    }
    return currentRow;
  });
  setData({ dataForLoanProduct: newData });
};

export const updateNotesDataForRow = ({
  rowIndex,
  columnId,
  newValue,
  setData,
  currentData,
}: {
  rowIndex: number;
  columnId: string;
  newValue: string;
  setData: ({
    dataForLoanProduct,
  }: {
    dataForLoanProduct: VerificationEligibilityReceivableGQLType[];
  }) => void;
  currentData: VerificationEligibilityReceivableGQLType[];
}) => {
  const externalReceivableId = currentData[rowIndex].externalReceivableId;

  // since there may be multiple rows for the same receivable, per criteria
  // set all rows for the receivable to the same value
  const newData = _.map(currentData, (currentRow, index) => {
    if (currentRow.externalReceivableId === externalReceivableId) {
      return {
        ...currentData[index]!,
        [columnId]: newValue,
      };
    }
    return currentRow;
  });
  setData({ dataForLoanProduct: newData });
};

const customSortingFn: SortingFn<VerificationEligibilityReceivableGQLType> = (
  rowA,
  rowB,
  columnId,
) => {
  const receivableIdForRowA: string = rowA.getValue(receivableIdColumnHeader);
  const receivableIdForRowB: string = rowB.getValue(receivableIdColumnHeader);

  const cellValueForRowA: string | number | boolean = rowA.getValue(columnId);
  const cellValueForRowB: string | number | boolean = rowB.getValue(columnId);

  // only sort within the same receivable
  if (receivableIdForRowA === receivableIdForRowB) {
    if (cellValueForRowA < cellValueForRowB) {
      return -1;
    }
    if (cellValueForRowA > cellValueForRowB) {
      return 1;
    }
  }

  return 0;
};

const customMultiFilter = (
  row: Row<VerificationEligibilityReceivableGQLType>,
  id: string,
  filterValues: Util.Primitive[],
) => {
  const rowValue: Util.Primitive = row.getValue(id);
  if (filterValues.length === 0 || filterValues.includes(rowValue)) {
    return true;
  }
  return false;
};

// strongly-typed version for the mandatory fields
export const formatReceivableEligibilityDifferenceColumnDef = (
  headerKey: typeof receivableEligibilityDifferenceColumnHeaders[number],
) => {
  return {
    accessorKey: headerKey,
    header: commonUtils.toTitleCase(headerKey),
    enableColumnFilter: false,
  };
};

// these columns should only be sorted within the same receivableId
// this ensures that the table rendering stays true at the receivable level
// with breakdown per eligibility criteria
export const formatIndividualEligibilityCriteriaColumnDef = ({
  accessorKey,
  header,
  enableColumnFilter,
  enableSorting,
}: {
  accessorKey: typeof individualEligibilityCriteriaColumnHeaders[number];
  header?: string; // in the case it should differ from accessorKey
  enableColumnFilter: boolean;
  enableSorting: boolean;
}) => {
  return {
    accessorKey,
    header: header ?? commonUtils.toTitleCase(accessorKey),
    sortingFn: enableSorting
      ? (
          rowA: Row<VerificationEligibilityReceivableGQLType>,
          rowB: Row<VerificationEligibilityReceivableGQLType>,
          columnId: string,
        ) => {
          return customSortingFn(rowA, rowB, columnId);
        }
      : undefined,
    enableSorting,
    filterFn: enableColumnFilter ? customMultiFilter : undefined,
    enableColumnFilter,
  };
};

// loosely-typed for any basefield
export const formatBasefieldColumnDef = (headerKey: string) => {
  return {
    accessorKey: headerKey,
    header: commonUtils.toTitleCase(headerKey),
    filterFn: customMultiFilter,
  };
};

export const createInitialColumnVisibility = ({
  verificationEligibilityReceivables,
  eligibilityRelatedBasefields = [],
}: {
  verificationEligibilityReceivables: VerificationEligibilityReceivableGQLType[];
  eligibilityRelatedBasefields?: string[];
}) => {
  const initialColumnVisibility: { [key: string]: boolean } = {};
  const oneEligibilityReceivable = _.first(verificationEligibilityReceivables);
  _.forEach(oneEligibilityReceivable, (_field, key) => {
    const includesDefaultHeaders = _.includes(
      receivableEligibilityDifferenceColumnHeaders,
      key,
    );

    initialColumnVisibility[key] = includesDefaultHeaders;
  });

  _.forEach(oneEligibilityReceivable?.baseFields, (_baseField, key) => {
    const isEligibilityRelatedBasefield = _.includes(
      eligibilityRelatedBasefields,
      key,
    );

    initialColumnVisibility[key] = isEligibilityRelatedBasefield;
  });

  return initialColumnVisibility;
};

export const createBasefieldsColumns = (
  verificationEligibilityReceivables: VerificationEligibilityReceivableGQLType[],
) => {
  const baseFields = _.first(verificationEligibilityReceivables)?.baseFields;
  const basefieldsColumns: ColumnDef<VerificationEligibilityReceivableGQLType>[] = _.map(
    baseFields,
    (_baseFieldValue, baseFieldKey) => {
      return formatBasefieldColumnDef(baseFieldKey);
    },
  );

  return basefieldsColumns;
};

export const createReceivableEligibilityData = (
  verificationEligibilityReceivables: VerificationEligibilityReceivableGQLType[],
): VerificationEligibilityReceivableGQLType[] => {
  return _.map(verificationEligibilityReceivables, (receivable) => {
    return {
      ...receivable.baseFields,
      ...receivable,
    };
  });
};

export const formatReceivableEligibilitySaveProgressChangeset = (verificationEligibilityReceivablesForAllLoanProducts: {
  [loanProductId: string]: VerificationEligibilityReceivableGQLType[];
}): UpdateFundingVerificationReceivableEligibilityChangesetGQLType[] => {
  return _.flatMap(
    verificationEligibilityReceivablesForAllLoanProducts,
    (loanProductReceivables) =>
      _.map(
        loanProductReceivables,
        ({ id, waived, note, fundingVerificationReportId }) => ({
          id,
          waived,
          note,
          fundingVerificationReportId,
        }),
      ),
  );
};

export const parseEligibilityTestsFormulasAndBasefieldsFromCreditAgreement = ({
  creditAgreementDocument,
  receivableEligibility,
}: {
  creditAgreementDocument: CreditAgreement.DomainEntity.CreditAgreementFullArtifact;
  receivableEligibility: VerificationEligibilityReceivableGQLType[];
}): { formulas: { [key: string]: string }; basefields: string[] } | null => {
  const {
    borrowingBaseMechanics: {
      calcAgentMechanics: { loanProductMechanics },
    },
  } = creditAgreementDocument;

  if (_.isEmpty(loanProductMechanics)) {
    return null;
  }

  const relevantKeysForReceivableEligibilityDifferences = new Set();

  _.forEach(receivableEligibility, ({ eligibilityCriteriaKey }) => {
    relevantKeysForReceivableEligibilityDifferences.add(eligibilityCriteriaKey);
  });

  const formulas: { [key: string]: string } = {};

  _.forEach(loanProductMechanics, ({ eligibilityTests }) => {
    return _.forEach(eligibilityTests, ({ _config_ }) => {
      if (
        _config_ &&
        _config_.eligibilityTestConfig &&
        relevantKeysForReceivableEligibilityDifferences.has(
          _config_.eligibilityTestConfig.propertyKey,
        ) &&
        _config_.eligibilityTestConfig.config &&
        _config_.eligibilityTestConfig.type === 'formula'
      ) {
        formulas[_config_.eligibilityTestConfig.propertyKey] =
          _config_.eligibilityTestConfig.config.formula;
      }
      return null;
    });
  });

  const basefieldsPrefix = 'baseFields.';
  const basefields = _.chain(Object.values(formulas))
    .flatMap((formula) => [...inspectDependencies(formula).variables])
    .filter((variable) => _.startsWith(variable, basefieldsPrefix))
    .map((variable) => variable.slice(basefieldsPrefix.length))
    .uniq()
    .value();

  return { formulas, basefields };
};
